import { PlanogramModel } from './planogram.interfaces';

function _cloneDeep<T>(value: T): T {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Campos de enriquecimiento por producto que el BE puede omitir con ?slim=true */
export interface SlimSaveProdCacheEntry {
  itemId?: number;
  itemData: Record<string, unknown> | null;
  unitOfExhibition?: Record<string, unknown> | null;
  promotions?: unknown;
  // CATMAN-B035: layout/orientación cacheados para reponer solo si el BE los omite.
  fixedX?: number;
  fixedWidth?: number;
  isFixedEmptySpace?: boolean;
  rotation?: number;
  rotateY?: boolean;
  stacksLyingDown?: boolean;
}

export interface SlimSavePlanogramCache {
  prods: Map<string, SlimSaveProdCacheEntry>;
  promotionMaterial?: unknown[];
}

export interface SlimSaveMetrics {
  cacheSize: number;
  responseSizeBytes: number;
  afterReattachSizeBytes: number;
  prodCount: number;
  cacheMissCount: number;
  reattachHitCount: number;
}

/** Key estable por posición estructural (orden módulo/estante/prod), no por ids técnicos. */
export function getSlimSaveProdCacheKey(
  location: 'module' | 'float',
  modOrder: number | string,
  shelfOrder: number | string,
  prodOrder: number | string
): string {
  return `${location}:${String(modOrder)}:${String(shelfOrder)}:${String(prodOrder)}`;
}

function _resolveOrder(entity: any, fallbackIndex: number): number | string {
  if (entity?.order != null) return entity.order;
  return fallbackIndex;
}

/** Un campo se considera ausente (candidato a reponer desde cache) si es null/undefined. */
function _isMissing(value: unknown): boolean {
  return value === undefined || value === null;
}

/** Snapshot de los campos de layout/orientación que el BE podría omitir con ?slim=true. */
function _extractLayoutFields(prod: any): Partial<SlimSaveProdCacheEntry> {
  return {
    fixedX: prod?.fixedX,
    fixedWidth: prod?.fixedWidth,
    isFixedEmptySpace: prod?.isFixedEmptySpace,
    rotation: prod?.rotation,
    rotateY: prod?.rotateY,
    stacksLyingDown: prod?.stacksLyingDown,
  };
}

export function buildSlimSavePlanogramCache(planogram: PlanogramModel | null | undefined): SlimSavePlanogramCache {
  const prods = new Map<string, SlimSaveProdCacheEntry>();

  if (!planogram) {
    return { prods };
  }

  (planogram.modules || []).forEach((mod, modIdx) => {
    const modOrder = _resolveOrder(mod, modIdx);
    (mod?.shelfs || []).forEach((shelf, shelfIdx) => {
      const shelfOrder = _resolveOrder(shelf, shelfIdx);
      (shelf?.prods || []).forEach((prod, prodIdx) => {
        if (!prod) return;
        const prodOrder = _resolveOrder(prod, prodIdx);
        const key = getSlimSaveProdCacheKey('module', modOrder, shelfOrder, prodOrder);
        prods.set(key, {
          itemId: (prod.itemId ?? prod.itemData?.itemId) as number | undefined,
          itemData: prod.itemData ? _cloneDeep(prod.itemData) : null,
          unitOfExhibition: prod.unitOfExhibition ? _cloneDeep(prod.unitOfExhibition) : null,
          promotions: prod.promotions != null ? _cloneDeep(prod.promotions) : undefined,
          ..._extractLayoutFields(prod),
        });
      });
    });
  });

  (planogram.floats || []).forEach((floatItem, floatIdx) => {
    if (!floatItem) return;
    const floatOrder = _resolveOrder(floatItem, floatIdx);
    (floatItem.prods || []).forEach((prod, prodIdx) => {
      if (!prod) return;
      const prodOrder = _resolveOrder(prod, prodIdx);
      const key = getSlimSaveProdCacheKey('float', floatOrder, floatOrder, prodOrder);
      prods.set(key, {
        itemId: (prod.itemId ?? prod.itemData?.itemId) as number | undefined,
        itemData: prod.itemData ? _cloneDeep(prod.itemData) : null,
        unitOfExhibition: prod.unitOfExhibition ? _cloneDeep(prod.unitOfExhibition) : null,
        promotions: prod.promotions != null ? _cloneDeep(prod.promotions) : undefined,
        ..._extractLayoutFields(prod),
      });
    });
  });

  const promotionMaterial = planogram.promotionMaterial?.length > 0 ? _cloneDeep(planogram.promotionMaterial) : undefined;

  return { prods, promotionMaterial };
}

/** @deprecated Use buildSlimSavePlanogramCache */
export function buildItemDataCache(planogram: PlanogramModel | null | undefined): Map<string, SlimSaveProdCacheEntry> {
  return buildSlimSavePlanogramCache(planogram).prods;
}

/**
 * CATMAN-B035: rellena SOLO los campos que la respuesta del BE omitió (payload slim).
 * Si el BE devolvió el valor, es autoritativo y no debe pisarse con el cache pre-save:
 * pisarlo por clave estructural (order) desincronizaba itemData/layout tras guardar y
 * causaba el desplazamiento de espacios vacíos fijos (I1) y la pérdida de la opción de
 * rotar/voltear (I2). El itemId=0 es el stub que el BE envía en modo slim, se trata como ausente.
 *
 * OJO: cuando el BE omite MetaData (slim=true, FF CAT-S38-SLIM), el AutoMapper del
 * backend no deja `itemData`/`itemId` en null nada más — también resetea a valores
 * FALSY-PERO-PRESENTES (`isFixedEmptySpace: false`, `fixedX: 0`, `fixedWidth: 0`) los
 * campos derivados de MetaData, porque su ForMember depende de
 * "src.MetaData != null && src.MetaData.IsFixedEmptySpace". Por eso NO alcanza mirar
 * _isMissing() campo por campo para esos tres: hay que detectar que el prod completo
 * vino degradado (itemData nulo o itemId=0 stub) y, en ese caso, forzar el restore
 * desde el cache pre-save — que es justamente lo que se le pidió persistir al BE en
 * este mismo guardado, así que es dato confiable, no stale.
 */
function _applyCacheEntryToProd(prod: Record<string, unknown>, entry: SlimSaveProdCacheEntry): boolean {
  if (!prod || !entry) return false;

  const wasDegradedBySlim = _isMissing(prod.itemData) || prod.itemId === 0;

  if (entry.itemId != null && (wasDegradedBySlim || _isMissing(prod.itemId))) {
    prod.itemId = entry.itemId;
  }
  if (entry.itemData && _isMissing(prod.itemData)) {
    prod.itemData = _cloneDeep(entry.itemData);
  }
  if (entry.unitOfExhibition != null && _isMissing(prod.unitOfExhibition)) {
    prod.unitOfExhibition = _cloneDeep(entry.unitOfExhibition);
  }
  if (entry.promotions !== undefined && _isMissing(prod.promotions)) {
    prod.promotions = _cloneDeep(entry.promotions);
  }

  // Derivados de MetaData en el BE (IsFixedEmptySpace/FixedX/FixedWidth): se resetean a
  // false/0 -no a null- cuando el payload viene degradado, así que el restore se
  // dispara por wasDegradedBySlim, no solo por _isMissing.
  if (entry.fixedX != null && (wasDegradedBySlim || _isMissing(prod.fixedX))) {
    prod.fixedX = entry.fixedX;
  }
  if (entry.fixedWidth != null && (wasDegradedBySlim || _isMissing(prod.fixedWidth))) {
    prod.fixedWidth = entry.fixedWidth;
  }
  if (entry.isFixedEmptySpace != null && (wasDegradedBySlim || _isMissing(prod.isFixedEmptySpace))) {
    prod.isFixedEmptySpace = entry.isFixedEmptySpace;
  }
  if (entry.rotation != null && _isMissing(prod.rotation)) {
    prod.rotation = entry.rotation;
  }
  if (entry.rotateY != null && _isMissing(prod.rotateY)) {
    prod.rotateY = entry.rotateY;
  }
  if (entry.stacksLyingDown != null && _isMissing(prod.stacksLyingDown)) {
    prod.stacksLyingDown = entry.stacksLyingDown;
  }

  return !!entry.itemData;
}

export function reattachItemDataFromCache(
  planogram: PlanogramModel | null | undefined,
  cache: SlimSavePlanogramCache | Map<string, SlimSaveProdCacheEntry>
): { cacheMissCount: number; reattachHitCount: number } {
  const planogramCache: SlimSavePlanogramCache = cache instanceof Map ? { prods: cache } : cache;

  let cacheMissCount = 0;
  let reattachHitCount = 0;

  if (!planogram) {
    return { cacheMissCount, reattachHitCount };
  }

  if (planogramCache.promotionMaterial?.length && !(planogram.promotionMaterial?.length > 0)) {
    planogram.promotionMaterial = _cloneDeep(planogramCache.promotionMaterial) as PlanogramModel['promotionMaterial'];
  }

  (planogram.modules || []).forEach((mod, modIdx) => {
    const modOrder = _resolveOrder(mod, modIdx);
    (mod?.shelfs || []).forEach((shelf, shelfIdx) => {
      const shelfOrder = _resolveOrder(shelf, shelfIdx);
      (shelf?.prods || []).forEach((prod, prodIdx) => {
        if (!prod) return;
        const prodOrder = _resolveOrder(prod, prodIdx);
        const key = getSlimSaveProdCacheKey('module', modOrder, shelfOrder, prodOrder);
        const entry = planogramCache.prods.get(key);
        if (!entry?.itemData) {
          cacheMissCount++;
          return;
        }
        if (_applyCacheEntryToProd(prod as Record<string, unknown>, entry)) {
          reattachHitCount++;
        }
      });
    });
  });

  (planogram.floats || []).forEach((floatItem, floatIdx) => {
    if (!floatItem) return;
    const floatOrder = _resolveOrder(floatItem, floatIdx);
    (floatItem.prods || []).forEach((prod, prodIdx) => {
      if (!prod) return;
      const prodOrder = _resolveOrder(prod, prodIdx);
      const key = getSlimSaveProdCacheKey('float', floatOrder, floatOrder, prodOrder);
      const entry = planogramCache.prods.get(key);
      if (!entry?.itemData) {
        cacheMissCount++;
        return;
      }
      if (_applyCacheEntryToProd(prod as Record<string, unknown>, entry)) {
        reattachHitCount++;
      }
    });
  });

  return { cacheMissCount, reattachHitCount };
}

export function measurePlanogramBytes(planogram: unknown): number {
  try {
    const json = JSON.stringify(planogram ?? null);
    return json?.length ?? 0;
  } catch {
    return 0;
  }
}

export function countAllProds(planogram: PlanogramModel | null | undefined): number {
  if (!planogram) return 0;

  let count = 0;
  (planogram.modules || []).forEach(mod => {
    (mod?.shelfs || []).forEach(shelf => {
      count += (shelf?.prods || []).length;
    });
  });
  (planogram.floats || []).forEach(floatItem => {
    count += (floatItem?.prods || []).length;
  });
  return count;
}

export function reportSlimSaveMetrics(
  cacheSize: number,
  responseSizeBytes: number,
  afterReattachSizeBytes: number,
  prodCount: number,
  cacheMissCount = 0,
  reattachHitCount = 0
): SlimSaveMetrics {
  const metrics: SlimSaveMetrics = {
    cacheSize,
    responseSizeBytes,
    afterReattachSizeBytes,
    prodCount,
    cacheMissCount,
    reattachHitCount,
  };

  const logPayload = {
    ...metrics,
    responseKB: (responseSizeBytes / 1024).toFixed(2),
    afterReattachKB: (afterReattachSizeBytes / 1024).toFixed(2),
  };

  // Fase 1: métricas visibles en consola para validación PM
  console.info('[SlimSave]', logPayload);

  return metrics;
}
